import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveRateTab, setRatesTabs } from '../../../../../features/actuarial/rates-slice';
import { ComputationTabs, TypeOfActionProposal } from '../../../../../types/type';
import ConfirmDefaultModal from '../../../../global/modal/confirm-default-modal';
import { useRequirementUpload } from '../../../../../hooks/actions/use-requirement-upload';
import { selectAllFilesUploaded } from '../../../../../features/gms-proposal-forms/attachment/file-requirement.slice';
import ProposalInformation from './proposal/proposal-information';
import ProposalRequirements from './attachment/proposal-requirements';
import ProposalGroupChatModal from './chats/proposal-group-chat-modal';
import { useComputationActions } from '../../../../../hooks/actions/user-computation-actions';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import { useModalAccess } from '../../../../global/modal/use-modal-access';
import { clearConfirmTypeModal } from '../../../../../features/gms-state-and-modal/modal-slice';
import { setBasicInformation } from '../../../../../features/gms-proposal-forms/basic-information/basic-information.slice';
import { useProposalData } from '../../../../../hooks/populate-data/use-proposal-data';

interface Props {
    typeOfIcon: TypeOfActionProposal;
}

interface ProposalTab {
    key: ComputationTabs;
    label: string;
    description: string;
    visible: boolean;
    disabled?: boolean;
}

export default function ProposalContentModal({ typeOfIcon }: Props) {
    
    const { populateProposalPreview } = useProposalData();
    const dispatch = useDispatch();
    const { 
        isMarketingGMSDepartment, 
        isDraftProposal, 
        isPrototype, 
        tinNumber, 
        secNumber 
    } = useProposalTypeData()
    const { 
        isConfirmReleaseProposal, 
        isConfirmBookedProposal, 
        isConfirmMissingTinAndSec 
    } = useModalAccess()
    const activeTab = useSelector(selectActiveRateTab);
    const isDone = useSelector(selectAllFilesUploaded);

    const [shouldScrollChatToBottom, setShouldScrollChatToBottom] = useState(false);
    const { 
        handleReleaseProposal, 
        handleCloseModalRelease, 
        isLoadingReleaseProposal 
    } = useComputationActions();
    const { 
        handleCloseModalConfirmBookedProposal, 
        handleBookedProposal, 
        isLoadingBookedProposal,
        handleSaveTinOrSecNumber,
        isLoadingUpdateTINorSEC,
    } = useRequirementUpload();
    const isShowAttachmentTab = isMarketingGMSDepartment && !isPrototype;
    const isLoading = isLoadingReleaseProposal || isLoadingBookedProposal;

    const handleCancelTinOrSecNumber = () => {
        dispatch(setBasicInformation({ field: 'tinNumber', value: '' }));
        dispatch(setBasicInformation({ field: 'secNumber', value: '' }));
        dispatch(clearConfirmTypeModal())
        populateProposalPreview();
    }

    const tabs: ProposalTab[] = [
        {
            key: 'info',
            label: 'Proposal',
            description: 'Proposal information',
            visible: true,
            disabled: false,
        },
        {
            key: 'attachment',
            label: 'Attachment',
            description: 'Supporting documents',
            visible: isShowAttachmentTab,
            disabled: isDraftProposal,
        },
        {
            key: 'chats',
            label: 'Group Chat',
            description: 'Collaborate with your team',
            visible: true,
            disabled: isDraftProposal,
        },
    ];

    const visibleTabs = tabs.filter((tab) => tab.visible);

    // Prevent stale attachment tab when prototype mode hides the tab.
    useEffect(() => {
        if (!isShowAttachmentTab && activeTab === 'attachment') {
            dispatch(setRatesTabs('info'));
        }
    }, [activeTab, dispatch, isShowAttachmentTab]);

    const handleSelectTab = (tab: ComputationTabs) => {
        dispatch(setRatesTabs(tab));

        if (tab === 'chats') {
            setShouldScrollChatToBottom((current) => !current);
        }
    };

    const renderTabIcon = (tab: ComputationTabs, active: boolean) => {
        const iconClass = active ? 'text-white' : 'text-[#186cb2]';

        if (tab === 'info') {
            return (
                <svg className={iconClass} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <path d="M8 7h8" />
                    <path d="M8 11h8" />
                    <path d="M8 15h5" />
                </svg>
            );
        }

        if (tab === 'attachment') {
            return (
                <svg className={iconClass} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
            );
        }

        return (
            <svg className={iconClass} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a8.38 8.38 0 0 1-9 9 8.38 8.38 0 0 1-9-9 8.38 8.38 0 0 1 9-9 8.38 8.38 0 0 1 9 9Z" />
                <path d="M8 12h8" />
                <path d="M8 8h5" />
                <path d="M8 16h6" />
            </svg>
        );
    };

    const renderMobileContent = () => {
        if (activeTab === 'info') {
            return <ProposalInformation typeOfIcon={typeOfIcon} />;
        }

        if (activeTab === 'attachment' && isShowAttachmentTab) {
            return <ProposalRequirements />;
        }

        return <ProposalGroupChatModal shouldScrollToBottom={shouldScrollChatToBottom} />;
    };

    const renderDesktopContent = () => {
        if (activeTab === 'chats') {
            return (
                <div className="h-full min-h-0">
                    <ProposalGroupChatModal shouldScrollToBottom={shouldScrollChatToBottom} />
                </div>
            );
        }

        return (
            <div className="grid h-full min-h-0 grid-cols-1 gap-2">
                <div className="min-h-0">
                    {activeTab === 'info' && (
                        <ProposalInformation typeOfIcon={typeOfIcon} />
                    )}

                    {activeTab === 'attachment' && isShowAttachmentTab && (
                        <div className="flex h-full min-h-0 flex-col">
                            <ProposalRequirements />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex h-full min-h-0 flex-col bg-white">
            {/* ================================================= */}
            {/* TABS */}
            {/* ================================================= */}
            <div className="shrink-0 border-b border-gray-200 bg-white px-2 pt-2 md:px-3 lg:px-4">
                <div className={`grid w-full overflow-hidden rounded-t-lg border border-gray-200 bg-gray-100 ${visibleTabs.length === 2 ? 'grid-cols-2' : visibleTabs.length === 1 ? 'grid-cols-1' : 'grid-cols-3'}`}>
                    {visibleTabs.map((tab) => {
                        const isActive = activeTab === tab.key;

                        return (
                            <button
                                key={tab.key}
                                disabled={tab.disabled}
                                type="button"
                                onClick={() => handleSelectTab(tab.key)}
                                className={`group relative flex min-w-0 items-center justify-center gap-1.5 px-2 py-2.5 text-[11px] font-medium transition-all duration-150 md:gap-2 ${
                                    tab.disabled
                                        ? 'cursor-not-allowed bg-gray-100 text-gray-300 opacity-60'
                                        : isActive
                                            ? 'bg-[#186cb2] text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                }`}
                            >
                                {renderTabIcon(tab.key, isActive && !tab.disabled)}
                                <span className="truncate">
                                    {tab.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ================================================= */}
            {/* MOBILE / TABLET */}
            {/* ================================================= */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2 md:p-3 lg:hidden">
                <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
                    {renderMobileContent()}
                </div>
            </div>
            {/* <div className="min-h-0 flex-1 p-2 md:p-3 lg:hidden">
                <div className="h-full min-h-0">
                    {renderMobileContent()}
                </div>
            </div> */}

            {/* ================================================= */}
            {/* DESKTOP */}
            {/* ================================================= */}
            <div className="hidden min-h-0 flex-1 p-3 lg:block">
                {renderDesktopContent()}
            </div>

            {/* ================================================= */}
            {/* RELEASE MODAL */}
            {/* ================================================= */}
            {isConfirmReleaseProposal && (
                <ConfirmDefaultModal
                    onCancel={handleCloseModalRelease}
                    onConfirm={handleReleaseProposal}
                    modalTitle="Release Proposal"
                    modalSubTitle="Are you sure you want to release this proposal?"
                    isLoading={isLoading}
                />
            )}

            {/* ================================================= */}
            {/* BOOK MODAL */}
            {/* ================================================= */}
            {isConfirmBookedProposal && (
                <ConfirmDefaultModal
                    isSubmitBooked={true}
                    onCancel={handleCloseModalConfirmBookedProposal}
                    onConfirm={handleBookedProposal}
                    modalTitle="Book Proposal"
                    modalSubTitle={
                        isDone
                            ? 'Are you sure you want to book this proposal?'
                            : 'Are you sure you want to book this proposal even though the required supporting documents are incomplete or have not been uploaded?'
                        }
                    isLoading={isLoading}
                />
            )}
            {isConfirmMissingTinAndSec && (
                <ConfirmDefaultModal
                    isDeny={false}
                    secNumber={secNumber}
                    tinNumber={tinNumber}
                    onCancel={handleCancelTinOrSecNumber}
                    onConfirm={handleSaveTinOrSecNumber}
                    isMissingTinAndSec={true}
                    modalTitle="Missing TIN or SEC Number"
                    modalSubTitle="Please provide either a TIN or SEC number before booking this proposal."
                    isLoading={isLoadingUpdateTINorSEC}
                />
            )}
        </div>
    );
}